/**
 * Image cache utility
 * 
 * Provides a persistent in-memory cache for preloaded provider images.
 * This cache persists across component remounts and tab changes,
 * preventing images from being reloaded unnecessarily.
 * 
 * Uses a module-level Map to store images, which persists as long as
 * the JavaScript context is alive (across tab switches within same session).
 */

/**
 * In-memory cache for provider images
 * Key: provider ID, Value: image URL or null
 */
const imageCache = new Map<string, string | null>();

/**
 * Get cached image URL for a provider
 * 
 * @param providerId - Provider ID to look up
 * @returns Cached image URL or undefined if not cached
 */
export const getCachedImage = (providerId: string): string | null | undefined => {
  return imageCache.get(providerId);
};

/**
 * Set cached image URL for a provider
 * 
 * @param providerId - Provider ID
 * @param imageUrl - Image URL to cache (or null if no image)
 */
export const setCachedImage = (providerId: string, imageUrl: string | null): void => {
  imageCache.set(providerId, imageUrl);
};

/**
 * Batch set cached images from a record
 * 
 * @param imageMap - Record of provider ID to image URL mappings
 */
export const setCachedImages = (imageMap: Record<string, string | null>): void => {
  Object.entries(imageMap).forEach(([providerId, imageUrl]) => {
    imageCache.set(providerId, imageUrl);
  });
};

/**
 * Get all cached images as a record
 * 
 * @returns Record of provider ID to image URL mappings
 */
export const getAllCachedImages = (): Record<string, string | null> => {
  const result: Record<string, string | null> = {};
  imageCache.forEach((imageUrl, providerId) => {
    result[providerId] = imageUrl;
  });
  return result;
};

/**
 * Clear cached image for a specific provider
 * 
 * @param providerId - Provider ID to clear
 */
export const clearCachedImage = (providerId: string): void => {
  imageCache.delete(providerId);
};

/**
 * Clear all cached images
 */
export const clearAllCachedImages = (): void => {
  imageCache.clear();
};

/**
 * Check if an image is cached for a provider
 * 
 * @param providerId - Provider ID to check
 * @returns True if image is cached (even if null)
 */
export const hasCachedImage = (providerId: string): boolean => {
  return imageCache.has(providerId);
};

